import { api } from './api';
import type {
  AttendanceStatus,
  ClassSummary,
  StudentRecord,
  StudentMonthlyAttendance,
} from '@/types';
import type {
  AvailableYearsResponse,
  TeacherAttendanceTrendResponse,
  TeacherCalendarData,
  TeacherDashboardResponse,
  TeacherYearlyOverviewResponse,
} from '@/types/teacher';

type SubmissionPayload = Array<{ studentId: string; status: AttendanceStatus }>;

export type TeacherAttendanceHistory = {
  range: {
    startDate: string;
    endDate: string;
  };
  summaries: Array<{
    date: string;
    present: number;
    absent: number;
    late: number;
    leave: number;
    editable: boolean;
  }>;
  totals: {
    present: number;
    absent: number;
    late: number;
    leave: number;
  };
};

export type TeacherAttendanceDetails = Array<{
  classId: string;
  className: string;
  submissions: Array<{
    studentId: string;
    studentName: string;
    rollNumber: string;
    photo?: string | null;
    status: AttendanceStatus;
  }>;
  editable: boolean;
}>;

export type TeacherPromotionBoard = {
  homeroom: ClassSummary | null;
  students: StudentRecord[];
  classes: ClassSummary[];
};

export type TeacherProfileResponse = {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  loginPassword?: string | null;
  phone?: string | null;
  address?: string | null;
  qualifications?: string | null;
  photo?: string | null;
  assignedClasses: Array<{
    id: string;
    name: string;
    studentCount: number;
  }>;
};

export const teacherService = {
  async getDashboard(params?: { classId?: string }) {
    const { data } = await api.get<TeacherDashboardResponse>('/teacher/dashboard', {
      params,
    });
    return data;
  },

  async saveDraft(payload: { date: string; classId: string; attendance: SubmissionPayload }) {
    await api.post('/teacher/attendance/draft', payload);
  },

  async listStudents(classId: string, params?: { month?: number; year?: number }) {
    const { data } = await api.get<StudentRecord[]>(`/teacher/classes/${classId}/students`, {
      params,
    });
    return data;
  },

  async submitAttendance(payload: { date: string; classId: string; attendance: SubmissionPayload }) {
    await api.post('/teacher/attendance/submit', payload);
  },

  async getHistory(params: { startDate?: string; endDate?: string }) {
    const { data } = await api.get<TeacherAttendanceHistory>('/teacher/attendance/history', {
      params,
    });
    return data;
  },

  async getNotifications() {
    const { data } = await api.get<TeacherDashboardResponse['notifications']>('/teacher/notifications');
    return data;
  },

  async getProfile() {
    const { data } = await api.get<TeacherProfileResponse>('/teacher/profile');
    return data;
  },

  async updatePassword(password: string) {
    const { data } = await api.put<{ message: string; loginPassword?: string }>('/teacher/password', {
      password,
    });
    return data;
  },

  async getStudentMonthlyAttendance(studentId: string, params?: { month?: number; year?: number }) {
    const { data } = await api.get<StudentMonthlyAttendance>(`/teacher/students/${studentId}/monthly`, {
      params,
    });
    return data;
  },

  async getAttendanceDetails(date: string) {
    const { data } = await api.get<unknown>('/teacher/attendance/details', {
      params: { date },
    });
    if (Array.isArray(data)) {
      return data as TeacherAttendanceDetails;
    }
    if (data && typeof data === 'object' && Array.isArray((data as { submissions?: unknown }).submissions)) {
      const row = data as {
        classId: string;
        className: string;
        submissions: TeacherAttendanceDetails[number]['submissions'];
        editable: boolean;
      };
      return [
        {
          classId: row.classId,
          className: row.className,
          submissions: row.submissions,
          editable: row.editable,
        },
      ];
    }
    return [];
  },
  async getAttendanceTrend(params?: { months?: number; classId?: string }) {
    const { data } = await api.get<TeacherAttendanceTrendResponse>('/teacher/attendance/trend', {
      params,
    });
    return data;
  },

  async getMonthlyOverview(params?: { year?: number; month?: number; classId?: string }) {
    const { data } = await api.get<TeacherCalendarData[]>('/teacher/attendance/monthly-overview', {
      params,
    });
    return Array.isArray(data) ? data : [];
  },

  async getYearlyOverview(params?: { year?: number; classId?: string }) {
    const { data } = await api.get<TeacherYearlyOverviewResponse>('/teacher/attendance/yearly-overview', {
      params,
    });
    return data;
  },

  async getAvailableYears() {
    const { data } = await api.get<AvailableYearsResponse>('/teacher/attendance/years');
    return data;
  },

  async getPromotionBoard() {
    const { data } = await api.get<TeacherPromotionBoard>('/teacher/promotion');
    return data;
  },

  async listPromotionDestinationStudents(classId: string) {
    const { data } = await api.get<StudentRecord[]>(`/teacher/promotion/classes/${classId}/students`);
    return Array.isArray(data) ? data : [];
  },

  async promoteStudents(payload: { studentIds: string[]; toClassId: string }) {
    const { data } = await api.post<{ updated: number }>('/teacher/promotion', payload);
    return data;
  },
};
